"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import clsx from "clsx";
import FormInput from "@/components/FormInput";
import IntlPhoneInput, { type IntlPhoneInputHandle } from "@/components/IntlPhoneInput";
import LottieLoader from "@/components/LottieLoader";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { submitContactMessageApi } from "../../services/contact.api";

type ContactFormData = {
  name: string;
  phone: string;
  email: string;
  message: string;
  whatsapp_group_name?: string;
};

export default function ContactForm() {
  const locale = useLocale();
  const t = useTranslations("web.contact");
  const phoneRef = useRef<IntlPhoneInputHandle>(null);
  const { isAuthenticated, user } = useCurrentUser();
  const isStudent = isAuthenticated && user?.account_type === "student";
  const [submitState, setSubmitState] = useState<{
    loading: boolean;
    error: string | null;
    success: string | null;
  }>({
    loading: false,
    error: null,
    success: null,
  });

  const schema = z.object({
    name: z.string().trim().min(1, t("validation_name")),
    phone: z.string().trim().min(1, t("validation_phone")),
    email: z.string().trim().email(t("validation_email")),
    message: z.string().trim().min(1, t("validation_message")),
    whatsapp_group_name: z.string().trim().optional(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      message: "",
      whatsapp_group_name: "",
    },
  });

  // Registered users don't need to retype their own contact details - once
  // useCurrentUser() resolves the account, fill them in and lock them so the
  // submitted message can't drift from the account of record.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    reset({
      ...getValues(),
      name: readString(user.name),
      email: readString(user.email),
      phone: readString(user.phone),
    });
  }, [isAuthenticated, user, reset, getValues]);

  const onSubmit = async (data: ContactFormData) => {
    setSubmitState({
      loading: true,
      error: null,
      success: null,
    });

    try {
      await submitContactMessageApi({
        ...data,
        whatsapp_group_name: isStudent ? data.whatsapp_group_name : undefined,
        locale,
      });

      reset();
      setSubmitState({
        loading: false,
        error: null,
        success: t("success"),
      });
    } catch {
      setSubmitState({
        loading: false,
        error: t("submission_failed"),
        success: null,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex h-full flex-col justify-between">
      {submitState.error ? (
        <div className="mb-4 rounded bg-danger/10 p-3 text-danger">{submitState.error}</div>
      ) : null}
      {submitState.success ? (
        <div className="mb-4 rounded bg-success/10 p-3 text-success">{submitState.success}</div>
      ) : null}

      <div className="flex flex-1 flex-col justify-between gap-4">
        <FormInput
          type="text"
          placeholder={t("full_name_placeholder")}
          error={errors.name?.message}
          register={register("name")}
          disabled={isAuthenticated}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <IntlPhoneInput
                ref={phoneRef}
                locale={locale}
                placeholder={t("phone_placeholder")}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.phone?.message}
                disabled={isAuthenticated}
              />
            )}
          />

          <FormInput
            type="email"
            placeholder={t("email_placeholder")}
            error={errors.email?.message}
            register={register("email")}
            disabled={isAuthenticated}
          />
        </div>

        {isStudent ? (
          <FormInput
            type="text"
            placeholder={t("whatsapp_group_name_placeholder")}
            error={errors.whatsapp_group_name?.message}
            register={register("whatsapp_group_name")}
          />
        ) : null}

        <div className="flex flex-1 flex-col">
          <textarea
            placeholder={t("message_placeholder")}
            className={clsx(
              "min-h-[140px] flex-1 w-full p-4 text-sm rounded-lg border bg-input text-foreground placeholder:text-text-muted transition-colors duration-150 focus:outline-none",
              errors.message
                ? "border-danger bg-danger/5"
                : "border-border",
              "focus:border-primary focus:ring-2 focus:ring-focus-ring/50",
              "disabled:bg-hover disabled:text-text-muted disabled:cursor-not-allowed disabled:border-border-subtle"
            )}
            {...register("message")}
          />
          {errors.message?.message ? (
            <p className="text-danger text-xs mt-1">{errors.message.message}</p>
          ) : null}
        </div>

        <div className="mt-2 text-center">
          <button
            type="submit"
            disabled={submitState.loading}
            className="w-full rounded-[10px] bg-primary px-8 py-3 text-white cursor-pointer transition-colors duration-200 hover:bg-secondary disabled:opacity-50"
          >
            {submitState.loading ? (
              <LottieLoader centered size={18} label={t("sending")} />
            ) : (
              t("send_message")
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}
